import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import {
  Sparkles, Heart, Moon, Mic, BookOpen, Video, TrendingUp,
  ClipboardCheck, Users, ArrowRight, Play, CheckCircle,
  BarChart3, FileText, Target, Shield, Loader2
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const categoryIcons = {
  Clinical: Mic,
  Operations: BarChart3,
  Family: Heart,
  Legacy: BookOpen,
  Compliance: ClipboardCheck,
};

const categoryColors = {
  Clinical: { bg: 'bg-sage-50', text: 'text-sage-400', border: 'border-sage-200' },
  Operations: { bg: 'bg-sky-50', text: 'text-sky-500', border: 'border-sky-200' },
  Family: { bg: 'bg-terra-50', text: 'text-terra-400', border: 'border-terra-200' },
  Legacy: { bg: 'bg-sky-50', text: 'text-sky-400', border: 'border-sky-200' },
  Compliance: { bg: 'bg-sage-50', text: 'text-sage-500', border: 'border-sage-200' },
};

export default function DemoLandingPage() {
  const [highlights, setHighlights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const [seeded, setSeeded] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');

  useEffect(() => {
    fetch(`${API}/api/demo-site/demo-highlights`)
      .then(r => r.json()).then(setHighlights)
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const seedData = async () => {
    setSeeding(true);
    try {
      await fetch(`${API}/api/demo-site/seed-demo-data`, { method: 'POST' });
      setSeeded(true);
      const res = await fetch(`${API}/api/demo-site/demo-highlights`);
      setHighlights(await res.json());
    } catch {}
    setSeeding(false);
  };

  const categories = ['all', 'Clinical', 'Operations', 'Family', 'Legacy', 'Compliance'];
  const filteredFeatures = highlights?.features?.filter(f => activeCategory === 'all' || f.category === activeCategory) || [];

  const demoSteps = [
    { step: 1, title: 'Ambient Documentation', path: '/ambient-documentation', desc: 'Paste sample visit notes, watch AI generate a SOAP note in seconds, then check quality score.', icon: Mic },
    { step: 2, title: 'AI Command Center', path: '/ai-hub', desc: 'Ask "Show me all patients with pain above 5" — get instant operational intelligence.', icon: Sparkles },
    { step: 3, title: 'Is This Normal?', path: '/ai-hub', desc: 'Select a patient, type "breathing sounds different tonight" — see the family triage response.', icon: Heart },
    { step: 4, title: 'Goodnight Check', path: '/ai-hub', desc: 'Generate an evening family summary. Warm, personalized, and ready to email or text.', icon: Moon },
    { step: 5, title: 'Life Story Companion', path: '/digital-legacy', desc: 'Start a conversation about a patient — AI asks gentle questions to build their life story.', icon: BookOpen },
    { step: 6, title: 'Census & Survey Readiness', path: '/operations-intelligence', desc: 'See real-time census, AI predictions, and one-click CMS survey compliance check.', icon: TrendingUp },
  ];

  return (
    <div className="min-h-screen bg-sand-100" data-testid="demo-landing-page">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-sage-50 via-sand-100 to-sky-50 opacity-80" />
        <div className="relative max-w-5xl mx-auto px-6 sm:px-8 py-16 sm:py-24">
          <div className="text-center">
            <Badge className="bg-sage-50 text-sage-400 rounded-full text-xs px-4 py-1 mb-4 inline-flex items-center gap-1.5">
              <Play className="w-3 h-3" /> Live Demo Environment
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-[#2C3D30] tracking-tight leading-tight">
              The Future of<br />Hospice Care
            </h1>
            <p className="mt-4 text-lg sm:text-xl text-[#5C6A5F] max-w-2xl mx-auto leading-relaxed">
              Goodwin Cares transforms hospice operations with AI that handles documentation,
              connects families, and predicts what your patients need — before they ask.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/ambient-documentation">
                <Button className="rounded-full bg-sage-400 hover:bg-sage-500 text-white px-8 py-3 text-base" data-testid="demo-start-btn">
                  <Play className="w-4 h-4 mr-2" /> Start the Demo
                </Button>
              </Link>
              {!seeded && (
                <Button onClick={seedData} disabled={seeding} variant="outline"
                  className="rounded-full border-sand-300 text-[#5C6A5F] px-8 py-3 text-base" data-testid="demo-seed-btn">
                  {seeding ? <><Loader2 className="w-4 h-4 animate-spin mr-2" />Loading demo data...</>
                    : <><Users className="w-4 h-4 mr-2" />Load Demo Patients</>}
                </Button>
              )}
              {seeded && (
                <Badge className="bg-sage-50 text-sage-400 rounded-full px-4 py-2 text-sm inline-flex items-center gap-1.5">
                  <CheckCircle className="w-4 h-4" /> Demo patients loaded
                </Badge>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Live Stats */}
      {highlights && (
        <div className="max-w-5xl mx-auto px-6 sm:px-8 -mt-4">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            {[
              { label: 'Active Patients', value: highlights.active_patients, color: 'sage' },
              { label: 'AI Notes Generated', value: highlights.ambient_notes_generated, color: 'sky' },
              { label: 'Goodnight Checks', value: highlights.goodnight_checks_sent, color: 'terra' },
              { label: 'Memories Captured', value: highlights.memories_captured, color: 'sky' },
              { label: 'Life Stories', value: highlights.life_stories_written, color: 'sage' },
            ].map((stat, i) => (
              <div key={i} className={`bg-sand-50 rounded-2xl p-4 border border-sand-200 text-center`} data-testid={`demo-stat-${i}`}>
                <p className="text-2xl font-heading font-bold text-[#2C3D30]">{stat.value}</p>
                <p className="text-xs text-[#5C6A5F] mt-0.5">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Demo Walkthrough */}
      <div className="max-w-5xl mx-auto px-6 sm:px-8 py-16">
        <div className="text-center mb-10">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-[#2C3D30] tracking-tight">6-Minute Demo Walkthrough</h2>
          <p className="text-sm text-[#5C6A5F] mt-2">Follow these steps to experience the platform's most powerful features</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="demo-steps">
          {demoSteps.map(step => (
            <Link key={step.step} to={step.path}
              className="group bg-sand-50 rounded-2xl p-6 border border-sand-200 hover:border-sage-200 hover:-translate-y-1 hover:shadow-md transition-all duration-300"
              data-testid={`demo-step-${step.step}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-8 h-8 rounded-full bg-sage-50 flex items-center justify-center text-sm font-heading font-bold text-sage-400">
                  {step.step}
                </div>
                <div className="w-8 h-8 rounded-xl bg-sage-50 flex items-center justify-center">
                  <step.icon className="w-4 h-4 text-sage-400" />
                </div>
              </div>
              <h3 className="text-sm font-heading font-semibold text-[#2C3D30] mb-1">{step.title}</h3>
              <p className="text-xs text-[#5C6A5F] leading-relaxed">{step.desc}</p>
              <div className="mt-3 flex items-center gap-1 text-xs text-sage-400 group-hover:translate-x-1 transition-transform">
                Try it <ArrowRight className="w-3 h-3" />
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Feature Grid */}
      <div className="max-w-5xl mx-auto px-6 sm:px-8 pb-16">
        <div className="text-center mb-8">
          <h2 className="text-2xl sm:text-3xl font-heading font-bold text-[#2C3D30] tracking-tight">Full Feature Suite</h2>
          <p className="text-sm text-[#5C6A5F] mt-2">11 AI-powered capabilities across Clinical, Operations, Family, and Legacy</p>
        </div>

        <div className="flex justify-center gap-2 mb-6 flex-wrap">
          {categories.map(cat => (
            <button key={cat} onClick={() => setActiveCategory(cat)}
              className={`px-4 py-2 rounded-full text-xs font-medium transition-all duration-300 ${
                activeCategory === cat ? 'bg-sage-400 text-white' : 'bg-sand-200 text-[#5C6A5F] hover:bg-sand-300'
              }`} data-testid={`demo-filter-${cat}`}>
              {cat === 'all' ? 'All Features' : cat}
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="demo-features">
          {filteredFeatures.map((feature, i) => {
            const colors = categoryColors[feature.category] || categoryColors.Clinical;
            const Icon = categoryIcons[feature.category] || FileText;
            return (
              <Link key={i} to={feature.path}
                className="group bg-sand-50 rounded-2xl p-5 border border-sand-200 hover:border-sage-200 hover:-translate-y-0.5 hover:shadow-sm transition-all duration-300"
                data-testid={`demo-feature-${i}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-xl ${colors.bg} flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`w-4 h-4 ${colors.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="text-sm font-heading font-semibold text-[#2C3D30]">{feature.name}</h3>
                      <Badge className={`${colors.bg} ${colors.text} rounded-full text-xs`}>{feature.category}</Badge>
                    </div>
                    <p className="text-xs text-[#5C6A5F] leading-relaxed">{feature.description}</p>
                  </div>
                  <ArrowRight className="w-4 h-4 text-sand-300 group-hover:text-sage-400 group-hover:translate-x-1 transition-all flex-shrink-0 mt-1" />
                </div>
              </Link>
            );
          })}
        </div>
      </div>

      {/* CTA Footer */}
      <div className="bg-sage-50 border-t border-sage-100">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 py-12 text-center">
          <h2 className="text-xl sm:text-2xl font-heading font-bold text-[#2C3D30] mb-2">Ready to transform your hospice?</h2>
          <p className="text-sm text-[#5C6A5F] mb-6">Goodwin Cares deploys in under 2 weeks. Let's talk.</p>
          <div className="flex gap-3 justify-center">
            <Link to="/ambient-documentation">
              <Button className="rounded-full bg-sage-400 hover:bg-sage-500 text-white px-6" data-testid="demo-cta-start">
                <Play className="w-4 h-4 mr-1.5" /> Explore the Demo
              </Button>
            </Link>
            <Button variant="outline" className="rounded-full border-sage-200 text-sage-600 px-6" data-testid="demo-cta-contact">
              <Users className="w-4 h-4 mr-1.5" /> Contact Sales
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
